import {
    useApplyCouponMutation,
    useGetCouponOffersQuery,
    useRemoveDiscountMutation
} from '@/src/api/rtkQuery/couponApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import { useGetTagsQuery } from '@/src/api/rtkQuery/tagsApi'
import { domainCountry } from '@/src/constants/domain'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import type { CouponDetail, CouponErrorDetails, InputField } from '@/src/types/couponTray'
import type { SearchCriteria } from '@/src/types/itinerary'

import { sampleCouponOffersEG, sampleCouponOffersFlyin } from '@/src/components/desktop/itinerary/couponTray/sampleData'
import { CashbackSchedule } from '@/src/types/couponTray'
import { isEgDomain } from '@/src/utils/generalUtils'
import { formatCouponMessages } from '@/src/utils/itinerary/couponUtils'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'
import useClevertapForItinerary from '../analytics/clevertap/itinerary'
import useFormatCurrencyForItinerary from '../currency/useFormatCurrencyForItinerary'
import useFormatDate from '../date/useFormatDate'
import useItineraryGtmEvent from '../analytics/gtm/itinerary'

const initialInputField = (): InputField => ({
    value: '',
    isApplied: false,
    error: null,
    pinDetails: { required: false, pinValue: '' }
})

const useCouponTray = (onSuccessfulApply?: (message: string) => void) => {
    const [typedInputField, setInputField] = useState<InputField>(initialInputField())
    const [errorDetails, setErrorDetails] = useState<CouponErrorDetails>({ error: false, msg: '', index: 0 })

    // Determine domain type
    const isEgyptDomain = isEgDomain()

    const t = useTranslations('couponTray')
    const tVariants = useTranslations('variants')
    const locale = useLocale()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const { formatDate } = useFormatDate()
    const itineraryId = useGetItineraryId()
    const [applyCoupon, { isLoading: isApplying }] = useApplyCouponMutation()
    const [removeDiscount] = useRemoveDiscountMutation()
    const {
        couponDetails,
        searchCriteria,
        isLoading: isItineraryLoading
    } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data, isLoading }) => ({
                couponDetails: data?.itineraryData?.couponDetails || [],
                searchCriteria: (data?.itineraryData?.searchCriteria || {}) as SearchCriteria,
                isLoading
            })
        }
    )
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { pushCustomEventToDataLayer } = useItineraryGtmEvent()

    // Determine trip type and international flag for tags API
    const isRT = !!searchCriteria?.arrivalDate
    const isIntl = searchCriteria?.international || true

    // Use tags API to get offers
    const { data: tagsData = [], isLoading: isLoadingOffers } = useGetTagsQuery({
        domain: domainCountry,
        ...(isRT ? { isRT: true } : { isOW: true }),
        ...(isIntl ? { intl: true } : { dom: true })
    })

    // Transform tags data to offer format with fallback to en if ar is empty
    const offers = tagsData.map(tag => {
        return {
            id: tag._id,
            icon: tag.icon,
            logo: tag.icon, // Legacy property
            code: tag.code,
            knowMoreLink: tag.targetLink,
            en: {
                heading: tag.en?.heading || '',
                discountText: tag.en?.heading || '',
                description: tag.en?.description || ''
            },
            ar: {
                heading: tag.ar?.heading || tag.en?.heading || '',
                discountText: tag.ar?.heading || tag.en?.heading || '',
                description: tag.ar?.description || tag.en?.description || ''
            }
        }
    })
    const appliedCouponCode = couponDetails[0]?.couponCode || ''
    const successMessages = formatCouponMessages(couponDetails, formatCurrency)
    const inputField = couponDetails[0]?.couponCode || ''
    const walletPromoDetail = couponDetails.find((couponDetail: CouponDetail) => couponDetail.walletPromo)

    const extractCashbackSchedule = (walletPromoDetail: CouponDetail): CashbackSchedule => {
        const { message_values, amount, currency, breakup = [] } = walletPromoDetail
        const date = message_values?.date || ''
        const wallet = message_values?.wallet || ''
        const variant = tVariants(wallet.toLowerCase())

        let message = ''
        if (breakup.length > 0) {
            const b = breakup[0]
            message = t('cashbackWalletMessage', {
                cashbackAmount: formatCurrency(b.amount),
                variant,
                date: formatDate(b.date)
            })
        } else if (amount && wallet && date) {
            message = t('cashbackWalletMessage', {
                cashbackAmount: formatCurrency(amount),
                variant,
                date: formatDate(date)
            })
        } else {
            message = walletPromoDetail.detailedMessage || walletPromoDetail.message || ''
        }

        return {
            message,
            date,
            amount,
            breakup,
            currency
        }
    }

    const cashbackSchedule = walletPromoDetail ? extractCashbackSchedule(walletPromoDetail) : null

    const handleInputChange = (_: number, value: string) => {
        setInputField(field => ({ ...field, value, error: null }))
        setErrorDetails({ error: false, msg: '', index: 0 })
    }

    const handleApply = async ({ code, source, index }: { code: string; source: string; index: number }) => {
        if (!code) {
            setErrorDetails({ error: true, msg: t('emptyError'), index: 0 })
            return
        }
        const value = code.trim()
        setInputField(field => ({ ...field, value: code }))
        const actionName = source === 'couponTray' ? 'quick_coupon_applied' : 'coupon_applied'

        try {
            const response = await applyCoupon({
                itineraryId,
                payload: { couponCodes: [value], validatePayments: false }
            })

            if (response?.data && Array.isArray(response?.data?.couponData)) {
                triggerItineraryEvent('Air_UI_Action', {
                    action_type: 'BUTTON',
                    action_name: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: true
                })
                pushCustomEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: true
                })
                setErrorDetails({ error: false, msg: '', index: 0 })
                // Handle successful apply with success message
                // Get the success message from the API response
                const appliedCouponData = response.data.couponData[0]
                let message = t('successMessage')
                if (appliedCouponData?.message) {
                    message = appliedCouponData.message
                    if (message.includes('<amount>')) {
                        message = message.replace('<amount>', String(formatCurrency(appliedCouponData.amount)))
                    }
                }
                onSuccessfulApply?.(message)
            } else if (response?.data && Array.isArray(response?.data?.errorCouponData)) {
                const errorMessage = response.data.errorCouponData[0]?.message || 'Something went wrong'
                triggerItineraryEvent('Air_Failures', {
                    coupon_name: value,
                    coupon_index: index,
                    failure_reason: errorMessage,
                    quick_coupon_applied: actionName === 'quick_coupon_applied' ? 'yes' : 'no',
                    failure_type: 'coupon_failure'
                })
                pushCustomEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: false
                })
                setErrorDetails({
                    error: true,
                    msg: errorMessage,
                    index: 0
                })
            } else {
                triggerItineraryEvent('Air_UI_Action', {
                    coupon_name: value,
                    coupon_index: index,
                    failure_reason: 'Something went wrong',
                    quick_coupon_applied: actionName === 'quick_coupon_applied' ? 'yes' : 'no',
                    failure_type: 'coupon_failure'
                })
                pushCustomEventToDataLayer({
                    event: actionName,
                    coupon_name: value,
                    coupon_index: index,
                    is_success: false
                })
                setErrorDetails({
                    error: true,
                    msg: 'Something went wrong',
                    index: 0
                })
            }
        } catch (e) {
            triggerItineraryEvent('Air_Failures', {
                coupon_name: value,
                coupon_index: index,
                failure_reason: 'Something went wrong',
                quick_coupon_applied: actionName === 'quick_coupon_applied' ? 'yes' : 'no',
                failure_type: 'coupon_failure'
            })
            pushCustomEventToDataLayer({
                event: actionName,
                coupon_name: value,
                coupon_index: index,
                is_success: false
            })
            setErrorDetails({ error: true, msg: 'Something went wrong', index: 0 })
        }
    }

    const handleRemove = async (_: number) => {
        try {
            const response = await removeDiscount({
                itineraryId,
                payload: { remove: ['COUPON'] }
            })

            if (response?.data?.data?.coupon === 'success') {
                setInputField(initialInputField())
                setErrorDetails({ error: false, msg: '', index: 0 })
            } else {
                setErrorDetails({ error: true, msg: 'Failed to remove coupon', index: 0 })
            }
        } catch (e) {
            setErrorDetails({ error: true, msg: 'Something went wrong', index: 0 })
        }
    }

    const handleKnowMore = (_offerId: string) => {}
    return {
        inputField,
        typedInputField,
        offers,
        onInputChange: handleInputChange,
        onApply: handleApply,
        onRemove: handleRemove,
        onKnowMore: handleKnowMore,
        formatCurrency,
        isLoading: isItineraryLoading || isLoadingOffers,
        isApplying,
        successMessages,
        appliedCouponCode,
        errorDetails,
        t,
        cashbackSchedule
    }
}

export default useCouponTray
